import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export type Lang = 'pt-BR' | 'es' | 'en' | 'de' | 'fr';

export const DEFAULT_LANG: Lang = 'pt-BR';
export const SUPPORTED_LANGS: Lang[] = ['pt-BR', 'es', 'en', 'de', 'fr'];
export const LANG_LABELS: Record<Lang, string> = {
  'pt-BR': 'Português',
  es: 'Español',
  en: 'English',
  de: 'Deutsch',
  fr: 'Français',
};

function extractLangFromPath(pathname: string): Lang {
  const first = pathname.split('/')[1];
  if (SUPPORTED_LANGS.includes(first as Lang)) return first as Lang;
  return DEFAULT_LANG;
}

interface I18nContextValue {
  lang: Lang;
  setLang: (lang: Lang) => void;
}

const I18nContext = createContext<I18nContextValue>({
  lang: DEFAULT_LANG,
  setLang: () => {},
});

export function I18nProvider({ children, initialLang }: { children: ReactNode; initialLang?: Lang }) {
  const location = useLocation();
  const navigate = useNavigate();
  // Render inicial DETERMINÍSTICO (bate 1:1 com o HTML prerenderizado p/ hidratação):
  // depende apenas da URL. localStorage/navegador são lidos após montar (efeito abaixo),
  // nunca no primeiro render — assim o cliente reproduz exatamente o markup do prerender.
  const [lang, setLangState] = useState<Lang>(() => {
    if (initialLang) return initialLang;
    const path = typeof window !== 'undefined' ? window.location.pathname : '/';
    return extractLangFromPath(path);
  });

  // Sincroniza lang com a URL (prefixo /es, /en, ...). Cobre navegação SPA.
  useEffect(() => {
    if (initialLang) return;
    const fromUrl = extractLangFromPath(location.pathname);
    if (fromUrl !== lang) {
      setLangState(fromUrl);
    }
  }, [location.pathname, initialLang]);

  // Preferência de idioma (localStorage → navegador) aplicada SOMENTE após a hidratação
  // e apenas na rota default (sem prefixo). Evita mismatch: o 1º render é sempre pt-BR
  // canônico; a troca automática para o idioma do visitante acontece depois, no cliente.
  useEffect(() => {
    if (initialLang) return;
    const fromUrl = extractLangFromPath(location.pathname);
    if (fromUrl !== DEFAULT_LANG) return; // rota com prefixo já é canônica
    let preferido: Lang | null = null;
    try {
      const stored = localStorage.getItem('vml-lang') as Lang | null;
      if (stored && SUPPORTED_LANGS.includes(stored)) preferido = stored;
    } catch {
      /* localStorage indisponível */
    }
    if (!preferido) {
      const b = navigator.language || '';
      if (b.startsWith('es')) preferido = 'es';
      else if (b.startsWith('en')) preferido = 'en';
      else if (b.startsWith('de')) preferido = 'de';
      else if (b.startsWith('fr')) preferido = 'fr';
    }
    if (preferido && preferido !== DEFAULT_LANG) {
      setLangState(preferido);
    }
    // roda uma vez após montar
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setLang = (next: Lang) => {
    setLangState(next);
    localStorage.setItem('vml-lang', next);
    // Troca prefixo na URL mantendo o restante do path
    const currentPath = location.pathname;
    const currentLang = extractLangFromPath(currentPath);
    let newPath: string;
    if (currentLang !== DEFAULT_LANG) {
      newPath = currentPath.replace(`/${currentLang}`, next === DEFAULT_LANG ? '' : `/${next}`);
    } else {
      newPath = next === DEFAULT_LANG ? currentPath : `/${next}${currentPath}`;
    }
    if (!newPath || newPath === '') newPath = '/';
    if (newPath !== currentPath) {
      navigate(newPath, { replace: true });
    }
  };

  return <I18nContext.Provider value={{ lang, setLang }}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  return useContext(I18nContext);
}

/** Retorna uma função que prefixa paths com o idioma atual */
export function useLocalizedPath() {
  const { lang } = useI18n();
  const prefix = lang === 'pt-BR' ? '' : `/${lang}`;
  return (path: string) => `${prefix}${path}`;
}
